import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/app/store'
import { Document } from '@/types/document.types'
import { groupDocumentsByFolder, FolderGroup } from '@/pages/documents/docHelper'

export const selectDocumentsLoading = (state: RootState) => state.documents.loadingDocuments
export const selectDocumentsError = (state: RootState) => state.documents.errorDocuments
export const selectDocuments = (state: RootState) => state.documents.documents
export const selectDownloadUrl = (state: RootState) => state.documents.downloadUrl
export const selectDownloadLoading = (state: RootState) => state.documents.isLoadingUrl
export const selectDownloadError = (state: RootState) => state.documents.errorUrl
export const selectUploadLoading = (state: RootState) => state.documents.isUploading
export const selectUploadError = (state: RootState) => state.documents.uploadError
export const selectUploadSuccess = (state: RootState) => state.documents.uploadSuccess
export const selectDeleteLoading = (state: RootState) => state.documents.isDeleting
export const selectDeleteError = (state: RootState) => state.documents.deleteError
export const selectDeleteSuccess = (state: RootState) => state.documents.deleteSuccess
export const selectFolderUploading = (state: RootState) => state.documents.isFolderUploading
export const selectFolderUploadError = (state: RootState) => state.documents.folderUploadError
export const selectFolderUploadProgress = (state: RootState) => state.documents.folderUploadProgress
export const selectFolderUploadResult = (state: RootState) => state.documents.folderUploadResult

export const selectDocumentsPagination = (state: RootState) => state.documents.pagination

export type DocumentVisibleUnit =
  | { kind: 'folder'; folder: FolderGroup }
  | { kind: 'file'; document: Document }

// Unidades visibles de la tabla: cada carpeta cuenta como 1 unidad (con sus N archivos internos)
// y cada archivo suelto cuenta como 1 unidad. Garantiza que una carpeta nunca aparezca duplicada
// entre páginas al paginar sobre esta lista.
const selectDocumentVisibleUnits = createSelector(
  [selectDocuments],
  (documents): DocumentVisibleUnit[] => {
    const { folders, standaloneFiles } = groupDocumentsByFolder(documents)
    return [
      ...folders.map((folder) => ({ kind: 'folder' as const, folder })),
      ...standaloneFiles.map((document) => ({ kind: 'file' as const, document })),
    ]
  }
)

export const selectDocumentsTotalCount = createSelector(
  [selectDocumentVisibleUnits],
  (units) => units.length
)

// Paginación cliente-side sobre las unidades visibles (carpetas + archivos sueltos) ya cargadas
// desde Firestore. No realiza fetch adicional.
export const selectPaginatedDocuments = createSelector(
  [selectDocumentVisibleUnits, selectDocumentsPagination],
  (units, { currentPage, pageSize }) =>
    units.slice((currentPage - 1) * pageSize, currentPage * pageSize)
)